import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material";

// Keep in sync with MaxDuplicateQuoteCopies in QuoteService.
const MAX_COPIES = 50;

/**
 * Dialog shown when the user clicks Duplicate on a quote, either from the
 * search results or from the quote detail view.
 *
 * Asks how many copies to create. Every copy carries over the source quote's
 * details and line items and gets its own new quote number.
 *
 * onConfirm receives the number of copies as a whole number.
 */
const DuplicateQuoteDialog = ({
  open,
  onClose,
  onConfirm,
  quoteNumber,
  loading = false,
}) => {
  // Held as a string so the field can be cleared while typing.
  const [copies, setCopies] = useState("1");

  // Reset every time the dialog opens for a (possibly different) quote
  useEffect(() => {
    if (open) {
      setCopies("1");
    }
  }, [open, quoteNumber]);

  const parsedCopies = Number.parseInt(copies, 10);
  const copiesValid =
    Number.isInteger(parsedCopies) &&
    parsedCopies >= 1 &&
    parsedCopies <= MAX_COPIES;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Duplicate Quote</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each copy carries over the details and line items from
          {quoteNumber ? ` Quote #${quoteNumber}` : " this quote"} and gets its
          own new quote number.
        </Typography>

        <TextField
          label="Number of Copies"
          type="number"
          size="small"
          value={copies}
          onChange={(e) => setCopies(e.target.value)}
          error={!copiesValid}
          helperText={
            copiesValid
              ? `Creates ${parsedCopies} new quote${
                  parsedCopies === 1 ? "" : "s"
                }.`
              : `Enter a whole number between 1 and ${MAX_COPIES}.`
          }
          inputProps={{ min: 1, max: MAX_COPIES, step: 1 }}
          autoFocus
          sx={{ width: 200 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onConfirm(parsedCopies)}
          disabled={!copiesValid || loading}
        >
          {loading ? "Duplicating..." : "Duplicate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateQuoteDialog;
